import { Product, ProductTenor } from '@protos/product';
import { Side } from '@protos/trading';
import { isLadderInfoEmpty, LadderBarScalingMode, LadderConfig, LadderPayload, LadderTabConfig } from '../LadderTypes';
import { orderBookDepthResponseToLiveLadderData } from './LadderDataTransforms';

import { WidgetSubStore } from '@features/common/store/StoreTypes';
import { WidgetPayload, WidgetProps } from '@features/dashboard/types';
import ApiClient from '@services/ApiClient';
import { create, StateCreator } from 'zustand';
import { useAppStore as appStore } from '../../../../common/store/AppStore';
import { emptyLadderDef, getLabelForTab, LadderInfo, LiveLadderData, storedLadderDefToLadderInfo } from '../LadderTypes';

// Ladder store definition
export interface LadderStore extends WidgetSubStore {
  orderAmount: number;
  setOrderAmount: (amount: number) => void;
  placeOrder: (price: number, product: Product, tenor: ProductTenor, side: Side, frontTenor?: ProductTenor, backTenor?: ProductTenor) => boolean;

  tabs: LadderInfo[];
  setTabs: (updatedTabs: LadderInfo[]) => void;

  tabIndex: number;
  setTabIndex: (index: number) => void;

  liveData: LiveLadderData[];

  config: LadderConfig;
  updateConfig: (update: Partial<LadderConfig>) => void;

  updateTab: (tabIndex: number, tabData: LadderInfo) => void;
  addTab: () => void;
  deleteTab: (tabIndex: number) => void;
  refreshCurrentTab: () => void;

  updateTabConfig: (index: number, update: Partial<LadderTabConfig>) => void;
}

// Makes a ladder store
export const makeLadderStore: StateCreator<LadderStore> = (set, get) => {
  const getRefreshedData = async (apiClient: ApiClient | undefined, info: LadderInfo): Promise<LiveLadderData | null> => {
    if (isLadderInfoEmpty(info)) return null;
    if (!apiClient) return null;

    const { tabs, liveData, config } = get();

    const index = tabs.indexOf(info);
    const lastData: LiveLadderData | undefined = index > -1 ? liveData[index] : undefined;

    const response = await apiClient?.getOrderBookDepth(info.product!.symbol);

    if (!response) return null;

    const newData: LiveLadderData | null = orderBookDepthResponseToLiveLadderData(
      response,
      info.product!,
      info.tenor!,
      info.exchange!,
      tabs.indexOf(info),
      lastData,
      config.barScaling
    );

    return newData;
  };

  return {
    products: [],
    tenors: [],
    apiClient: undefined,
    config: {
      barScaling: LadderBarScalingMode.LastFive,
      showAllTicks: false,
    },

    updateConfig: (update: Partial<LadderConfig>) => {
      set({ config: { ...get().config, ...update } });
    },

    updateTabConfig: (index: number, update: Partial<LadderTabConfig>) => {
      const tab = get().tabs[index];
      if (tab) {
        const updatedTab = { ...tab, config: { ...tab.config, ...update } };
        const newTabs = [...get().tabs];
        newTabs[index] = updatedTab;
        set({ tabs: newTabs });
      }
    },

    orderAmount: 1,
    setOrderAmount: (amount: number) => {
      set({ orderAmount: amount });
    },

    placeOrder: (price: number, product: Product, tenor: ProductTenor, side: Side, frontTenor?: ProductTenor, backTenor?: ProductTenor) => {
      return true;
    },

    tabIndex: 0,
    setTabIndex: async (index: number) => {
      const { tabIndex, tabs, liveData } = get();
      const { adminApiClient: apiClient } = appStore.getState();

      if (index === tabIndex || !apiClient) return;

      set({ tabIndex: index });

      const updatedLiveDataForTab = await getRefreshedData(apiClient, tabs[index]);

      if (updatedLiveDataForTab !== null) {
        const newLiveData = [...liveData];
        newLiveData[index] = updatedLiveDataForTab;
        set({ liveData: newLiveData });
      }
    },

    tabs: [],
    setTabs: (tabs: LadderInfo[]) => {
      set({ tabs });
    },

    liveData: [],

    initFromPayload: (payload: WidgetPayload) => {
      const { products, tenors, adminApiClient: apiClient } = appStore.getState();
      if (!products || !tenors || !apiClient) return false;

      if (!payload) return false;
      const { tabs } = payload as LadderPayload;

      const hydratedTabs = tabs.map((t, index) => {
        return storedLadderDefToLadderInfo(t, products, tenors, index);
      });

      if (hydratedTabs.length === 0) {
        hydratedTabs.push(storedLadderDefToLadderInfo({ symbol: null, tenorCode: null, exchange: null }, products, tenors, 0));
      }

      set({ tabs: hydratedTabs, tabIndex: 0 });

      return hydratedTabs.length > 0;
    },

    updateTab: async (tabIndex: number, tabData: LadderInfo) => {
      const { tabs, liveData } = get();
      const { adminApiClient: apiClient } = appStore.getState();

      if (!apiClient) {
        console.log('no api client...');
        return;
      }

      const current = tabs[tabIndex];
      if (current) {
        const { product, tenor, exchange } = tabData;

        const newTabs = [...tabs];
        const updated = { ...current, product, tenor, exchange, label: getLabelForTab({ product, tenor, exchange, index: current.index }) };
        newTabs[tabIndex] = updated;

        // Do initial update with empty data
        let newLiveData = [...liveData];
        newLiveData[tabIndex] = {
          index: tabIndex,
          ladder: [],
          total: 0,
          min: 0,
          max: 0,
          fairValueIndex: 0,
          lastUpdateTime: new Date().getTime(),
          lastMaxValues: [],
        };
        set({ tabs: newTabs, liveData: newLiveData });

        // Then get the actual data and refresh, assuming there is any
        const updatedLiveDataForTab = await getRefreshedData(apiClient, newTabs[tabIndex]);
        if (updatedLiveDataForTab === null) return;

        newLiveData = [...liveData];
        newLiveData[tabIndex] = updatedLiveDataForTab;
        set({ tabs: newTabs, liveData: newLiveData });
      }
    },

    addTab: () => {
      const { products, tenors } = appStore.getState();
      if (!products || !tenors) return;

      const { tabs, liveData } = get();
      const newTabIndex = tabs.length;
      const newTab: LadderInfo = storedLadderDefToLadderInfo(emptyLadderDef, products, tenors, newTabIndex);
      const newLiveData = [...liveData];
      set({ tabs: [...tabs, newTab], liveData: newLiveData, tabIndex: newTabIndex });
      return true;
    },

    deleteTab: (tabIndex: number) => {
      const { products, tenors } = appStore.getState();
      if (!products || !tenors) return;

      const { tabs, liveData, tabIndex: currentTabIndex } = get();
      const newTabs: LadderInfo[] = tabs
        .filter((t, i) => tabIndex !== i)
        .map((t, i) => {
          const updated = { ...t, index: i };
          return { ...updated, label: getLabelForTab(updated) };
        });

      let newTabIndex = currentTabIndex;

      if (newTabs.length === 0) {
        newTabs.push(storedLadderDefToLadderInfo(emptyLadderDef, products, tenors, 0));
        newTabIndex = 0;
      } else if (tabIndex >= newTabs.length) {
        newTabIndex = newTabs.length - 1;
      }

      const newLiveData = liveData.filter((d, i) => i !== tabIndex);

      set({ tabIndex: newTabIndex, tabs: newTabs, liveData: newLiveData });

      return true;
    },

    refreshCurrentTab: async () => {
      const { tabs, liveData, tabIndex } = get();

      const { adminApiClient: apiClient } = appStore.getState();

      if (tabs.length === 0) return;

      const currentTab = tabs[tabIndex];
      if (isLadderInfoEmpty(currentTab) || !apiClient) return;

      // Something is erroring from time to time...
      try {
        const updatedLiveDataForTab = await getRefreshedData(apiClient, tabs[tabIndex]);
        if (updatedLiveDataForTab === null) return;

        const newLiveData = [...liveData];
        newLiveData[tabIndex] = updatedLiveDataForTab;
        set({ liveData: newLiveData });
      } catch (e) {
        console.error('Ladder Store: refresh tab failed');
        console.error(e);
      }
    },
  };
};

export const makeAndPopulateLadderStore = (props: WidgetProps & { payload?: LadderPayload }) => {};

// For use as a standalone hook
export const useLadderStore = create<LadderStore>(makeLadderStore);
